import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { logger } from '../../../logger/logger';

// Direct imports for themes for tree shaking performance
import { lightTheme } from './theme-configs/light';
import { darkTheme } from './theme-configs/dark';

/**
 * Legacy theme types for backwards compatibility in the theme switch
 * @readonly
 * @enum {string}
 */
export const LEGACY_THEME_TYPES = {
    light: 'theme-light',
    dark: 'theme-dark',
};

/**
 * Names of the themes available in the app.
 * Add new themes here and in themesMap below.
 * @readonly
 * @enum {string}
 */
export const THEMES = {
    light: 'light',
    dark: 'dark',
};

/**
 * Map of theme names to their corresponding theme objects.
 * Add new themes here and in THEMES above.
 * each theme maps to a theme config file in ./theme-configs
 * @type {Object.<string, Theme>}
 */
export const themesMap = {
    [THEMES.light]: lightTheme,
    [THEMES.dark]: darkTheme,
};

/**
 * ThemeProvider is a wrapper component that provides theme context to styled-components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} [props.theme=THEMES.light] - Theme name from THEMES enum
 * @returns {JSX.Element} ThemeProvider component
 */
export const ThemeProvider = ({ children, theme = THEMES.light }) => {
    useEffect(() => {
        if (!themesMap[theme]) {
            logger.warn('Theme not found, falling back to light theme', {
                requestedTheme: theme,
                availableThemes: Object.keys(themesMap),
                component: 'ThemeProvider',
            });
        }
    }, [theme]);
    const selectedTheme = themesMap[theme] || themesMap.light;

    // Inject CSS variables into the document root
    useEffect(() => {
        if (selectedTheme.cssVariables) {
            const root = document.documentElement;
            Object.entries(selectedTheme.cssVariables).forEach(
                ([property, value]) => {
                    root.style.setProperty(property, value);
                }
            );

            // Cleanup function to remove CSS variables when component unmounts
            return () => {
                Object.keys(selectedTheme.cssVariables).forEach((property) => {
                    root.style.removeProperty(property);
                });
            };
        }
    }, [selectedTheme]);
    return (
        <StyledThemeProvider theme={selectedTheme}>
            {children}
        </StyledThemeProvider>
    );
};
ThemeProvider.propTypes = {
    children: PropTypes.node.isRequired,
    theme: PropTypes.oneOf(Object.values(THEMES)),
};
export default ThemeProvider;
